import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import useBrandMark from "../../hooks/useBrandMark";
import AuthBrandSide from "./AuthBrandSide";
import AuthBrandMark from "./AuthBrandMark";
import { cn } from "../ui";

/**
 * AuthLayout — the shell for every auth screen: /login, /signup,
 * /forgot-password (as a route element, filling its `<Outlet />`) and
 * /register-institute (as an ordinary wrapper around its children).
 *
 * Two halves on desktop: the dark brand panel and the form card. For the three
 * outlet routes the panel is rendered HERE rather than inside each form so it
 * stays mounted while the user moves between them — the hero photograph does
 * not reload and the copy does not flash. Only the `<Outlet />` swaps.
 *
 * ── The phone is not a narrower desktop ──────────────────────────────────────
 * Below `lg` the brand panel is dropped rather than stacked: it is half a screen
 * of marketing, and stacking it would push the first field below the fold. What
 * replaced it was a plain bar and a card with no background, which left the page
 * reading as an unstyled form on grey.
 *
 * So the phone gets its own compact version of the panel — the same dark surface
 * and the same brand wash, ~100px tall — and the card is a real card at every
 * width, lifted to overlap the band. That keeps the branding and the depth
 * without spending vertical space, which is the actual constraint here.
 *
 * The form column scrolls on its own (`overflow-y-auto`) so a long form — the
 * three-step password reset, the institute registration — never scrolls the
 * brand panel out of view on a 1280×720 laptop, which is the viewport most of
 * these users are on.
 *
 * ── Props (all optional; with none of them this is the login shell) ──────────
 *   children     — the form. Falls back to `<Outlet />` for the routed screens.
 *   brand        — { name, favicon, subtitle } pinning a brand instead of the
 *                  visited host's, for a page that creates an institute and so
 *                  has no institute branding it could legitimately wear.
 *   side         — props forwarded to AuthBrandSide (kicker/title/blurb/…).
 *   mobileLink   — { to, label } for the band's cross-link. Defaults to the
 *                  other door for the current route.
 *   mobileTagline— the line under the brand mark on the band.
 *   width        — "default" (30rem) | "wide" (34rem, for a longer form).
 *   footer       — node under the card, inside the scroll column.
 */

// The cross-link in the mobile band always points at the OTHER door, so it is
// never a link to the page you are already on.
const MOBILE_LINK = {
  "/login": { to: "/signup", label: "Sign Up" },
  "/signup": { to: "/login", label: "Sign In" },
  "/forgot-password": { to: "/login", label: "Sign In" },
};

const CARD_WIDTHS = {
  default: "tw-max-w-[30rem]",
  wide: "tw-max-w-[34rem]",
};

const AuthLayout = ({
  children,
  brand,
  side,
  mobileLink,
  mobileTagline,
  width = "default",
  footer,
}) => {
  // One rule for every brand slot (hooks/useBrandMark): on an institute's page
  // nothing falls back to the platform's name or mark.
  const mark = useBrandMark(brand);
  const { pathname } = useLocation();
  const link = mobileLink || MOBILE_LINK[pathname] || MOBILE_LINK["/login"];
  const tagline = mobileTagline || mark.subtitle;

  return (
    <div className="tw-flex tw-min-h-screen tw-bg-surface">
      <AuthBrandSide brand={brand} {...side} />

      {/* `surface-sunken`, not `surface`: --bg-primary is #fbfbfb in light and the
          card is #ffffff, so a card on it would have almost nothing to sit on. */}
      <main className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-bg-surface-sunken">
        {/* ── Mobile / tablet brand band ──────────────────────────────────────
            `surface-dark` for the same reason the desktop panel is: it is dark in
            both themes, so the ink on it is --on-surface-dark and not --text-*.
            The generous `pb` is the overlap allowance for the card below. */}
        <div className="tw-relative tw-overflow-hidden tw-bg-surface-dark tw-px-5 tw-pb-14 tw-pt-4 tw-text-on-surface-dark lg:tw-hidden">
          <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(125%_150%_at_100%_0%,hsl(var(--primary)/0.42)_0%,transparent_62%)]" />

          <div className="tw-relative tw-flex tw-items-center tw-justify-between tw-gap-3">
            <Link to="/" className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5 tw-no-underline">
              <AuthBrandMark mark={mark} size="md" className="tw-bg-on-surface-dark/10 tw-p-1" />
              <span className="tw-truncate tw-font-semibold tw-text-on-surface-dark">
                {mark.name}
              </span>
            </Link>
            <Link
              to={link.to}
              className="tw-shrink-0 tw-rounded-md tw-border tw-border-solid tw-border-on-surface-dark/25 tw-bg-on-surface-dark/10 tw-px-3.5 tw-py-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-on-surface-dark tw-no-underline"
            >
              {link.label}
            </Link>
          </div>

          {tagline && (
            <p className="tw-relative tw-mb-0 tw-mt-3 tw-text-[0.8125rem] tw-leading-snug tw-text-on-surface-dark/65">
              {tagline}
            </p>
          )}
        </div>

        <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-px-4 tw-pb-8 sm:tw-px-6 lg:tw-justify-center lg:tw-py-8">
          {/* The card. A real card at EVERY width — the phone version used to be
              transparent and borderless, which is what made it read as unstyled.
              It rides up over the band so the first field stays near the top.

              ⚠️ `tw-relative` is LOAD-BEARING, not layout. The band above is
              positioned, and a positioned element paints above a static one no
              matter which comes first in the DOM — so without this the band
              covers the top of the card it is supposed to sit behind, and the
              heading is sliced in half. Both are `z-index: auto`, so DOM order
              decides once the card is positioned too.

              ⚠️ `tw-rounded-lg` is 16px here: the radius scale is remapped in
              tailwind.config.js. */}
          <div
            className={cn(
              "tw-relative tw-w-full tw--mt-10 tw-rounded-lg tw-border tw-border-solid tw-border-line",
              "tw-bg-surface-raised tw-p-5 tw-shadow-card-lg",
              "sm:tw-p-8 md:tw-p-10 lg:tw-mt-0",
              CARD_WIDTHS[width] || CARD_WIDTHS.default
            )}
          >
            {children || <Outlet />}
          </div>

          {footer && (
            <div className="tw-mt-5 tw-w-full tw-max-w-[34rem] tw-text-center">{footer}</div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AuthLayout;
